import axios from 'axios';
import { baseURL } from '../Constants';

const url = `${baseURL}/OGCGuest`;

export interface OGCGuest {
    guestId: number;
    season: number;
    division: string;
    guestSteamId: string;
    guestName: string;
    invitedBySteamId: string;
    invitedByName: string;
    contentTeam: string;
    createdAt: string;
}

export interface OGCGuestRequest {
    season: number;
    division: string;
    guestSteamId: string;
    contentTeam: string;
}

// Get guest for a specific division
export const getOGCGuest = async (season: number, division: string, contentTeam: string = 'ogc'): Promise<{ hasGuest: boolean; guest?: OGCGuest }> => {
    try {
        const response = await axios.get(`${url}/get`, {
            params: { season, division, contentTeam }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching OGC guest:', error);
        return { hasGuest: false };
    }
};

// Get all guests for a season
export const getAllOGCGuests = async (season: number, contentTeam: string = 'ogc'): Promise<OGCGuest[]> => {
    try {
        const response = await axios.get(`${url}/getAll`, {
            params: { season, contentTeam }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching all OGC guests:', error);
        return [];
    }
};

export interface OGCGuestCandidate {
    steamId: string;
    playerName: string;
}

const bearer = (token: string | null | undefined) => (token ? { Authorization: `Bearer ${token}` } : undefined);

// Set a guest predictor (hosts and admins; the backend reads who you are from the login token).
// hostSteamId is still sent for a backend that predates the token check.
export const setOGCGuest = async (request: OGCGuestRequest, hostSteamId: string, contentTeam: string = 'ogc', token?: string | null): Promise<boolean> => {
    try {
        const response = await axios.post(`${url}/set`, request, {
            params: { hostSteamId, contentTeam },
            headers: bearer(token),
        });
        return response.status === 200;
    } catch (error) {
        console.error('Error setting OGC guest:', error);
        return false;
    }
};

// Remove a guest predictor (hosts and admins, from the login token)
export const removeOGCGuest = async (season: number, division: string, hostSteamId: string, contentTeam: string = 'ogc', token?: string | null): Promise<boolean> => {
    try {
        const response = await axios.delete(`${url}/remove`, {
            params: { season, division, hostSteamId, contentTeam },
            headers: bearer(token),
        });
        return response.status === 200;
    } catch (error) {
        console.error('Error removing OGC guest:', error);
        return false;
    }
};

// Everyone but the hosts with a prediction for the division, by name. Throws, so the picker can say it failed.
export const getOGCGuestCandidates = async (season: number, division: string, contentTeam: string): Promise<OGCGuestCandidate[]> => {
    const response = await axios.get(`${url}/candidates`, { params: { season, division, contentTeam } });
    return Array.isArray(response.data) ? response.data : [];
};

// Check if user can manage guests
export const canManageOGCGuests = async (steamId: string, contentTeam: string = 'ogc'): Promise<boolean> => {
    try {
        const response = await axios.get(`${url}/canManage`, {
            params: { steamId, contentTeam }
        });
        return response.data.canManage;
    } catch (error) {
        console.error('Error checking OGC management permission:', error);
        return false;
    }
};
